import React, { useCallback, useEffect, useRef, useState } from "react";
import { CalendarPlus, ChevronLeft, ChevronRight, Eye, FileSignature, LayoutGrid, Pencil, Plus, Search, Table2, Upload, UsersRound } from "lucide-react";
import {
  createCustomer,
  getCustomer,
  getSchedulingErrorMessage,
  listAppointments,
  searchCustomers,
  updateCustomer,
  uploadCustomerPicture,
} from "../../../api/schedulingApi";
import { Alert, Field, Modal, StatusPill, addDays, btnPrimary, btnSecondary, card, formatDate, formatDateTime, inputClass, todayIn } from "./ui";
import CustomerContacts from "./CustomerContacts";
import CustomerContactEditor, { contactPayload, emptyContact } from "./CustomerContactEditor";
import CustomerAvatar from "./CustomerAvatar";
import CustomerPictureInput from "./CustomerPictureInput";
import CustomerPictureModal from "./CustomerPictureModal";
import useAuth from "../../../hooks/useAuth";

const EMPTY = { name: "", gender: "", phone: "", wa_id: "", email: "", notes: "", date_of_birth: "", registration_number: "", dnd: false };
const SOURCE_LABELS = {
  admin: "Manual",
  whatsapp_inbound: "WhatsApp message",
  registration_form: "Registration form",
  csv_import: "CSV import",
  appointment_booking: "Appointment booking",
  whatsapp_business_app: "WhatsApp Business App",
  unknown: "Unknown",
};

export default function CustomersPanel({ botId, timeZone, providers, onHistory, onConsent, onAppointment, onImport, viewMode = "table", onViewModeChange, fetchAll = false, enableAppointmentFilter = false, refreshKey = 0 }) {
  const { can } = useAuth();
  const canCreate = can("customers.create");
  const canUpdate = can("customers.update");
  const canReadAppointments = can("appointments.read");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewing, setViewing] = useState(null);
  const [contactCustomer, setContactCustomer] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [registrationContacts, setRegistrationContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pictureFile, setPictureFile] = useState(null);
  const [pictureRemoved, setPictureRemoved] = useState(false);
  const [pictureBusy, setPictureBusy] = useState(false);
  const [createdCustomerId, setCreatedCustomerId] = useState(null);
  const [pictureCustomer, setPictureCustomer] = useState(null);
  const [formError, setFormError] = useState("");
  const [showAppointments, setShowAppointments] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(() => todayIn(timeZone));
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setError("");
    setLoading(true);
    try {
      const search = query.trim() || undefined;
      const allRows = [];
      let offset = 0;
      while (true) {
        const page = (await searchCustomers(botId, fetchAll ? { q: search, limit: 200, offset } : { q: search })) || [];
        if (requestId !== requestRef.current) return;
        allRows.push(...page);
        if (!fetchAll || page.length < 200) break;
        offset += page.length;
      }
      let customers = [...new Map(allRows.map((customer) => [customer.id, customer])).values()];
      if (enableAppointmentFilter && showAppointments && canReadAppointments) {
        const appointments = await listAppointments(botId, { date: appointmentDate });
        if (requestId !== requestRef.current) return;
        const customerIds = new Set((appointments || []).map((appointment) => String(appointment.customer_id)));
        customers = customers.filter((customer) => customerIds.has(String(customer.id)));
      }
      setRows(customers);
    } catch (err) {
      if (requestId === requestRef.current) {
        setRows([]);
        setError(getSchedulingErrorMessage(err, "Could not load customers."));
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [botId, fetchAll, query, enableAppointmentFilter, showAppointments, appointmentDate, canReadAppointments]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => {
      clearTimeout(t);
      requestRef.current += 1;
    };
  }, [load, refreshKey]);

  useEffect(() => {
    if (editing) setForm({
      name: editing.name || "",
      gender: editing.gender || "",
      phone: editing.phone || "",
      wa_id: editing.wa_id || "",
      email: editing.email || "",
      notes: editing.notes || "",
      date_of_birth: editing.date_of_birth || "",
      registration_number: editing.registration_number || "",
      dnd: !!editing.dnd,
    });
    setRegistrationContacts([]);
    setFormError("");
    setPictureFile(null);
    setPictureRemoved(false);
    setPictureBusy(false);
    setCreatedCustomerId(null);
  }, [editing]);

  const submit = async (e) => {
    e.preventDefault();
    if ((editing?.id ? !canUpdate : !canCreate) || saving || pictureBusy) return;
    setFormError("");
    if (!form.name.trim()) {
      setFormError("Enter a customer name.");
      return;
    }
    if (form.date_of_birth && form.date_of_birth > todayIn(timeZone)) {
      setFormError("Date of birth cannot be in the future.");
      return;
    }
    if (registrationContacts.some((contact) => !contact.relationship.trim() || (
      contact.mode === "existing" ? !contact.person : !contact.name.trim()
    ))) {
      setFormError("Select or enter a person and relationship for every contact, or remove the optional contact.");
      return;
    }
    const values = {
      name: form.name.trim() || null,
      gender: form.gender || null,
      phone: form.phone.trim() || null,
      wa_id: form.wa_id.trim() || null,
      email: form.email.trim() || null,
      notes: form.notes.trim() || null,
      date_of_birth: form.date_of_birth || null,
      registration_number: form.registration_number.trim() || null,
      dnd: form.dnd,
    };
    setSaving(true);
    let customerSaved = false;
    try {
      const id = editing.id || createdCustomerId;
      const customer = id ? await updateCustomer(botId, id, values)
        : await createCustomer(botId, { ...values, contacts: registrationContacts.map(contactPayload) });
      const savedId = id || customer.id;
      if (!id) setCreatedCustomerId(savedId);
      customerSaved = true;
      if (pictureFile) await uploadCustomerPicture(botId, savedId, pictureFile);
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(customerSaved
        ? `Customer saved, but the profile picture change failed. Retry Save; the customer will not be created again. ${getSchedulingErrorMessage(err, "Could not save picture.")}`
        : getSchedulingErrorMessage(err, "Could not save customer."));
    } finally {
      setSaving(false);
    }
  };

  const open = async (c) => {
    if (onHistory) {
      onHistory(c);
      return;
    }
    try {
      setViewing(await getCustomer(botId, c.id));
    } catch (err) {
      setError(getSchedulingErrorMessage(err));
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const providerName = (id) => providers.find((p) => p.id === id)?.name || `#${id}`;
  const editableAvatar = (customer, className) => <div className="relative shrink-0 self-start">
    <CustomerAvatar customer={customer} botId={botId} className={className} />
    {canUpdate && <button type="button" onClick={() => setPictureCustomer(customer)} aria-label={`Update profile picture for ${customer.name || "customer"}`}
      title="Update profile picture" className="absolute -bottom-1 -right-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:text-cyan-700">
      <Pencil size={13} />
    </button>}
  </div>;
  const customerActions = (customer) => <div className="flex shrink-0 flex-wrap items-center gap-2">
    <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => open(customer)} aria-label={`View ${customer.name || "customer"}`} title="View customer appointments"><Eye size={16} /></button>
    {canUpdate && <><button type="button" className={btnSecondary} onClick={() => setEditing(customer)} aria-label={`Edit ${customer.name || "customer"}`} title="Edit customer"><Pencil size={16} /></button>
      <button type="button" className={btnSecondary} onClick={() => setContactCustomer(customer)} aria-label={`Manage contacts for ${customer.name || "customer"}`} title="Manage contacts"><UsersRound size={16} /></button></>}
    {can("consent_requests.create") && onConsent && <button type="button" className={btnSecondary} onClick={() => onConsent(customer)} aria-label={`Send consent form to ${customer.name || "customer"}`} title="Send consent form"><FileSignature size={16} /></button>}
    {can("appointments.create") && onAppointment && <button type="button" className={btnSecondary} onClick={() => onAppointment(customer)} aria-label={`Book appointment for ${customer.name || "customer"}`} title="Book appointment"><CalendarPlus size={16} /></button>}
  </div>;

  return (
    <div className={`${card} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
          <input className="w-full max-w-sm rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, phone, email or registration no." aria-label="Search customers" />
        </div>
        <div className="flex items-center gap-2">
          {onViewModeChange && <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-1" role="group" aria-label="Customer view">
            <button type="button" onClick={() => onViewModeChange("table")} aria-label="Table view" title="Table view" aria-pressed={viewMode === "table"} className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${viewMode === "table" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Table2 size={16} />
            </button>
            <button type="button" onClick={() => onViewModeChange("card")} aria-label="Card view" title="Card view" aria-pressed={viewMode === "card"} className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${viewMode === "card" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <LayoutGrid size={16} />
            </button>
          </div>}
          {canCreate && <button type="button" className={btnPrimary} onClick={() => setEditing({})}><Plus size={16} /> Add customer</button>}
          {onImport && can("customers.read") && <button type="button" className={btnSecondary} onClick={onImport}
            aria-label="Import customers / history" title="Import customers / history"><Upload size={16} /></button>}
        </div>
      </div>
      {enableAppointmentFilter && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" checked={showAppointments} onChange={(event) => setShowAppointments(event.target.checked)} />
            Show Appointments
          </label>
          {showAppointments && (
            <div className="flex items-center gap-2">
              <button type="button" className={btnSecondary} aria-label="Previous appointment date" onClick={() => setAppointmentDate((date) => addDays(date, -1))}><ChevronLeft size={16} /></button>
              <input type="date" required aria-label="Appointment date" value={appointmentDate} onChange={(event) => { if (event.target.value) setAppointmentDate(event.target.value); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              <button type="button" className={btnSecondary} aria-label="Next appointment date" onClick={() => setAppointmentDate((date) => addDays(date, 1))}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>
      )}
      <Alert>{error}</Alert>
      {loading ? <p className="py-6 text-center text-sm text-slate-500">Loading customers…</p> : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">No customers found.</p>
      ) : viewMode === "card" ? (
        <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3" role="list" aria-label="Customer cards">
          {rows.map((customer) => (
            <article key={customer.id} role="listitem" aria-label={`${customer.name || "Unnamed"} customer`} className="flex min-w-0 flex-col gap-3 border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="break-words font-semibold text-slate-900"><button type="button" onClick={() => open(customer)} className="text-left hover:text-cyan-800 hover:underline" title="Open patient record">{customer.name || "Unnamed"}</button></h3>
                <p className="mt-1 truncate text-sm text-slate-600">{customer.phone || "No phone"}</p>
                <p className="mt-0.5 truncate text-sm text-slate-500">{customer.email || "No email"}</p>
                {customer.gender && <p className="mt-1 text-xs text-slate-500">Gender: {customer.gender.replaceAll("_", " ")}</p>}
                {customer.registration_number && <p className="mt-1 text-xs text-slate-500">Registration: {customer.registration_number}</p>}
                {customer.date_of_birth && <p className="mt-1 text-xs text-slate-500">DOB: {formatDate(customer.date_of_birth)}</p>}
                <p className="mt-1 text-xs text-slate-500">Source: {SOURCE_LABELS[customer.reg_source] || customer.reg_source || "Unknown"}</p>
                {customer.dnd && <p className="mt-1 text-xs font-semibold text-amber-700">DND preference recorded</p>}
              </div>
              {editableAvatar(customer, "h-20 w-20")}
              </div>
              <footer className="mt-auto flex justify-end border-t border-slate-100 pt-3">
                {customerActions(customer)}
              </footer>
            </article>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-left text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr><th className="py-2">Name / Registration</th><th>Phone</th><th>Email</th><th>Date of birth</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((c) => (
              <tr key={c.id}>
                <td className="py-2 font-semibold text-slate-900"><div className="flex items-center gap-3">{editableAvatar(c, "h-10 w-10")}<div><button type="button" onClick={() => open(c)} className="text-left font-semibold hover:text-cyan-800 hover:underline" title="Open patient record">{c.name || "Unnamed"}</button>{c.gender && <p className="text-xs font-normal capitalize text-slate-500">Gender: {c.gender.replaceAll("_", " ")}</p>}{c.registration_number && <p className="text-xs font-normal text-slate-500">{c.registration_number}</p>}{c.dnd && <p className="text-xs font-normal text-amber-700">DND preference recorded</p>}</div></div></td>
                <td>{c.phone || "—"}</td>
                <td>{c.email || "—"}</td>
                <td>{c.date_of_birth ? formatDate(c.date_of_birth) : "—"}</td>
                <td className="space-x-2 text-right">
                  {customerActions(c)}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      <Modal open={editing !== null} title={editing?.id || createdCustomerId ? "Edit customer" : "Add customer"} onClose={() => { if (!saving && !pictureBusy) setEditing(null); }} wide>
        <form className="space-y-4" onSubmit={submit}>
          <Alert>{formError}</Alert>
          <fieldset disabled={saving} className="space-y-4">
          {editing && !editing.id && <CustomerPictureInput key="new" customer={editing} botId={botId}
            file={pictureFile} removed={pictureRemoved} onBusyChange={setPictureBusy}
            onChange={(file, removed) => { setPictureFile(file); setPictureRemoved(removed); }} />}
          <Field label="Name"><input required maxLength={200} className={inputClass} value={form.name} onChange={set("name")} /></Field>
          <Field label="Gender"><select className={inputClass} value={form.gender} onChange={set("gender")}>
            <option value="">Not specified</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="nonbinary">Non-binary</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select></Field>
          <Field label="Phone" hint="Family members may share a number."><input maxLength={30} className={inputClass} value={form.phone} onChange={set("phone")} /></Field>
          <Field label="WhatsApp ID" hint="Links incoming WhatsApp messages to this customer."><input maxLength={30} className={inputClass} value={form.wa_id} onChange={set("wa_id")} /></Field>
          <Field label="Email"><input type="email" maxLength={255} className={inputClass} value={form.email} onChange={set("email")} /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date of birth" hint="Optional, including for minors."><input type="date" max={todayIn(timeZone)} className={inputClass} value={form.date_of_birth} onChange={set("date_of_birth")} /></Field>
            <Field label="Registration number" hint="Optional, up to 10 characters."><input maxLength={10} className={inputClass} value={form.registration_number} onChange={set("registration_number")} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" checked={form.dnd} onChange={(event) => setForm((previous) => ({ ...previous, dnd: event.target.checked }))} />DND — opt out of birthday and occasion greetings</label>
          <p className="text-xs text-slate-500">Records the preference for optional greetings and promotional messages, not appointment or consent messages. Automatic delivery enforcement will be added with the scheduler.</p>
          <Field label="Notes"><textarea className={inputClass} rows={3} value={form.notes} onChange={set("notes")} /></Field>
          {!editing?.id && !createdCustomerId && <section className="space-y-3 border-t border-slate-200 pt-4" aria-label="Registration contacts">
            <h4 className="font-semibold text-slate-900">Parents / contacts (optional)</h4>
            <p className="text-xs text-slate-500">You can save a minor without a parent or primary contact.</p>
            {registrationContacts.map((contact, index) => <div key={index} className="space-y-3 rounded-xl border border-slate-200 p-3">
              <h5 className="font-semibold">Contact {index + 1}</h5>
              <CustomerContactEditor botId={botId} value={contact} disabled={saving} onChange={(next) => setRegistrationContacts((previous) => previous.map((item, itemIndex) => itemIndex === index ? next : next.is_primary ? { ...item, is_primary: false } : item))} />
              <button type="button" className={btnSecondary} onClick={() => setRegistrationContacts((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}>Remove contact {index + 1}</button>
            </div>)}
            <button type="button" className={btnSecondary} disabled={registrationContacts.length >= 50} onClick={() => setRegistrationContacts((previous) => [...previous, emptyContact()])}>Add parent / contact</button>
          </section>}
          <div className="flex justify-end gap-2">
            <button type="button" className={btnSecondary} disabled={pictureBusy} onClick={() => setEditing(null)}>Cancel</button>
            <button type="submit" className={btnPrimary} disabled={pictureBusy}>{saving ? "Saving..." : "Save"}</button>
          </div>
          </fieldset>
        </form>
      </Modal>

      {pictureCustomer && <CustomerPictureModal key={pictureCustomer.id} customer={pictureCustomer} botId={botId}
        onClose={() => setPictureCustomer(null)} onSaved={(updated) => {
          const mergePicture = (customer) => ({ ...customer,
            profile_picture_file_id: updated.profile_picture_file_id,
            profile_picture_url: updated.profile_picture_url,
            profile_picture_expires_at: updated.profile_picture_expires_at,
          });
          setRows((previous) => previous.map((customer) => customer.id === updated.id ? mergePicture(customer) : customer));
          setViewing((previous) => previous?.id === updated.id ? mergePicture(previous) : previous);
        }} />}

      <Modal open={contactCustomer !== null} title={`Contacts for ${contactCustomer?.name || "customer"}`} onClose={() => { if (!contactSaving) setContactCustomer(null); }} wide>
        {contactCustomer && <CustomerContacts key={`${botId}:${contactCustomer.id}`} botId={botId} customer={contactCustomer} onBusyChange={setContactSaving} />}
      </Modal>
      <Modal open={viewing !== null} title={`${viewing?.name || "Customer"} · appointments`} onClose={() => setViewing(null)} wide>
        {viewing && editableAvatar(viewing, "h-16 w-16")}
        {viewing?.appointments?.length ? (
          <ul className="divide-y divide-slate-100">
            {[...viewing.appointments].reverse().map((a) => (
              <li key={a.id} className="flex items-center justify-between py-2 text-sm">
                <span>{formatDateTime(a.start_at, timeZone)} · {providerName(a.provider_id)}</span>
                <StatusPill status={a.status} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">No appointments yet.</p>
        )}
      </Modal>
    </div>
  );
}
